import React, { createContext, useContext, useState, useEffect } from 'react';

const CompanyContext = createContext({
  code: '',
  name: '',
  shortName: '',
  legalName: '',
  address: '',
  registrationNumber: '',
  logo: null,
  primaryColor: '#1976d2',
  loading: true,
});

export function isSuperAdminPath() {
  return window.location.pathname.startsWith('/superadmin');
}

const HOST_COMPANY_CODES = {
  'localhost': 'ATS001',
  '127.0.0.1': 'ATS001',
  'payroll.adityatrading.in': 'ATS001',
  'payroll.capitalbridge.one': 'CB002',
  'payroll.clearcover.in': 'CC004',
  'payroll.studiosilq.in': 'SS003',
};

const hostCompanyCode = () => HOST_COMPANY_CODES[window.location.hostname.toLowerCase()] || '';

// Known custom domains must resolve synchronously. Waiting for the branding
// request before setting this value blocks the whole application and can make
// initial API calls fall back to ATS when that request is slow.
let _resolvedCode = hostCompanyCode().toLowerCase();

export function getCompanyCode() {
  if (isSuperAdminPath()) return '';
  const parts = window.location.pathname.split('/').filter(Boolean);
  const first = parts[0] || '';
  // A first segment counts as a company code only if it contains a digit
  // (ATS001, CB002) — that's how it is told apart from a page path such as
  // /home or /login. The backend enforces this when a company is created
  // (superadmin company_list_create), so a letters-only code cannot exist.
  if (first && /\d/.test(first)) return first;
  return '';
}

// Logo/media paths come back either absolute (S3: https://..., or
// protocol-relative //...) or relative to the API host (/media/...).
export function resolveMediaUrl(path, base) {
  if (!path) return undefined;
  // An http:// asset on an https page is mixed content (browser warning or
  // blocked image) — upgrade it; S3 serves both schemes.
  if (/^http:\/\//i.test(path) && window.location.protocol === 'https:') {
    return path.replace(/^http:/i, 'https:');
  }
  if (/^(https?:)?\/\//i.test(path)) return path;
  return `${String(base || '').replace(/\/$/, '')}${path.startsWith('/') ? '' : '/'}${path}`;
}

export function getEffectiveCompanyCode() {
  return getCompanyCode() || _resolvedCode;
}

// API base for the current company: "https://hr.../ats001/". Shared by both
// axios instances and hand-built URLs so the prefix rule lives in one place.
export function tenantApiBase(rawBase) {
  const code = getEffectiveCompanyCode();
  return code ? `${String(rawBase).replace(/\/$/, '')}/${code}/` : rawBase;
}

function generatedCompanyFavicon(company) {
  const label = (company.shortName || company.name || company.code || 'HR')
    .trim()
    .charAt(0)
    .toUpperCase() || 'H';
  const escapedLabel = label
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
  const color = /^#[0-9a-f]{6}$/i.test(company.primaryColor || '')
    ? company.primaryColor
    : '#1976d2';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="14" fill="${color}"/><text x="32" y="43" text-anchor="middle" font-family="Arial,sans-serif" font-size="36" font-weight="700" fill="white">${escapedLabel}</text></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

export function CompanyProvider({ children }) {
  const code = getCompanyCode();
  const domainCode = hostCompanyCode();
  const cacheKey = `company-branding:${window.location.hostname.toLowerCase()}`;
  let cachedCompany = null;
  try {
    cachedCompany = JSON.parse(window.localStorage.getItem(cacheKey) || 'null');
  } catch (_) {
    cachedCompany = null;
  }
  const expectedCode = (code || domainCode).toUpperCase();
  if (!expectedCode || cachedCompany?.code?.toUpperCase() !== expectedCode) {
    cachedCompany = null;
  }
  const [company, setCompany] = useState({
    code: cachedCompany?.code || code || domainCode,
    name: cachedCompany?.name || '',
    shortName: cachedCompany?.shortName || '',
    legalName: cachedCompany?.legalName || '',
    address: cachedCompany?.address || '',
    registrationNumber: cachedCompany?.registrationNumber || '',
    logo: cachedCompany?.logo || null,
    primaryColor: cachedCompany?.primaryColor || '#1976d2',
    loading: true,
  });

  useEffect(() => {
    const baseURL =
      process.env.REACT_APP_API_URL || 'https://hr.adityatrading.in/';
    const mediaBase = baseURL.replace(/\/$/, '');

    const lookupCode = code || domainCode;
    const url = lookupCode
      ? `${baseURL}api/public/company/${lookupCode}/`
      : `${baseURL}api/public/company-by-domain/?domain=${encodeURIComponent(window.location.hostname)}`;

    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 8000);

    // Company branding can be updated while an admin tab is already open.
    // Never reuse a cached public-company response, otherwise a replaced logo
    // or address can remain stale even after the page is refreshed.
    fetch(url, { cache: 'no-store', signal: controller.signal })
      .then((res) => (res.ok ? res.json() : Promise.reject(res)))
      .then((data) => {
        if (lookupCode && data.code?.toUpperCase() !== lookupCode.toUpperCase()) {
          throw new Error(`Company mismatch: expected ${lookupCode}`);
        }
        if (!code && data.code) _resolvedCode = data.code.toLowerCase();
        const logoUrl = resolveMediaUrl(data.logo, mediaBase) || null;
        const resolvedCompany = {
          code: data.code || lookupCode,
          name: data.name || '',
          shortName: data.short_name || '',
          legalName: data.legal_name || '',
          address: data.address || '',
          registrationNumber: data.registration_number || '',
          logo: logoUrl,
          primaryColor: data.primary_color || '#1976d2',
          loading: false,
        };
        setCompany(resolvedCompany);
        try {
          window.localStorage.setItem(cacheKey, JSON.stringify(resolvedCompany));
        } catch (_) {
          // Storage can be unavailable in strict/private browser modes.
        }
      })
      .catch(() =>
        setCompany((prev) => ({ ...prev, loading: false }))
      )
      .finally(() => window.clearTimeout(timeoutId));

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [code, domainCode, cacheKey]);

  // Keep browser chrome tenant-aware as well as the in-app header. Company
  // logos can be PNG/JPEG/SVG URLs; when none is configured, generate a small
  // coloured initial so different company tabs are still distinguishable.
  useEffect(() => {
    if (company.loading || (!company.code && !company.name)) return;

    let favicon = document.querySelector('link[rel~="icon"]');
    if (!favicon) {
      favicon = document.createElement('link');
      favicon.rel = 'icon';
      favicon.id = 'company-favicon';
      document.head.appendChild(favicon);
    }
    favicon.href = company.logo || generatedCompanyFavicon(company);

    const appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleTouchIcon) {
      appleTouchIcon.href = company.logo || generatedCompanyFavicon(company);
    }

    const titleName = company.shortName || company.name || company.code;
    document.title = `${titleName} - HR Portal`;
  }, [company]);

  // No code in the URL (live domain, e.g. payroll.capitalbridge.one): hold the
  // app until the domain lookup has set _resolvedCode. Otherwise the first API
  // calls go out without the company prefix, land on the default company and
  // are rejected for another company's login. A failed lookup also ends
  // loading, so the app still renders (as the default company).
  if (!code && !_resolvedCode && company.loading && !isSuperAdminPath()) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', color: '#666' }}>
        Loading…
      </div>
    );
  }

  return (
    <CompanyContext.Provider value={company}>{children}</CompanyContext.Provider>
  );
}

export function useCompany() {
  return useContext(CompanyContext);
}

export default CompanyContext;
